import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import KpiCard from "@/components/KpiCard";
import IdBadge from "@/components/Badge";
import ExpenseForm from "@/components/ExpenseForm";
import EmployeeDocuments from "@/components/EmployeeDocuments";
import EmployeeHealth from "@/components/EmployeeHealth";
import { toast } from "sonner";
import {
  LogIn, LogOut, Coffee, Play, Clock, Timer, CalendarDays, Hourglass,
  Loader2, Plus, ShieldCheck, CheckCircle2, Home, Building2, Wallet, Briefcase, Sparkles,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const ABS_TYPES = [
  { v: "vacation", l: "Vacaciones" }, { v: "sick", l: "Baja médica" },
  { v: "personal", l: "Asuntos propios" }, { v: "other", l: "Otros" },
];
const ABS_LABEL = Object.fromEntries(ABS_TYPES.map((t) => [t.v, t.l]));
const STATUS = {
  pending: { l: "Pendiente", c: "bg-amber-100 text-amber-700" },
  approved: { l: "Aprobada", c: "bg-emerald-100 text-emerald-700" },
  rejected: { l: "Rechazada", c: "bg-rose-100 text-rose-700" },
};

function fmtMin(min) {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export default function Portal() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [badge, setBadge] = useState(null);
  const [lopd, setLopd] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [req, setReq] = useState({ type: "vacation", start_date: "", end_date: "", reason: "" });
  const [profile, setProfile] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [workMode, setWorkMode] = useState("office");
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expOpen, setExpOpen] = useState(false);
  const tick = useRef();

  const loadAll = useCallback(async () => {
    const [st, su, sh, ab] = await Promise.all([
      api.get("/clock/status"), api.get("/me/summary"), api.get("/me/shifts"), api.get("/me/absences"),
    ]);
    setStatus(st.data); setSummary(su.data); setShifts(sh.data); setAbsences(ab.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => { api.get("/me/badge").then(({ data }) => setBadge(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/lopd/me").then(({ data }) => setLopd(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/me/profile").then(({ data }) => setProfile(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/me/schedule").then(({ data }) => setSchedule(data.schedule)).catch(() => {}); }, []);

  const loadExpenses = useCallback(() => { api.get("/me/expenses").then(({ data }) => setExpenses(data)).catch(() => {}); }, []);
  useEffect(() => { loadExpenses(); }, [loadExpenses]);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/expense-categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);

  const acceptLopd = async () => {
    setAccepting(true);
    try {
      await api.post("/lopd/me/consent");
      const { data } = await api.get("/lopd/me");
      setLopd(data);
      toast.success("Consentimiento registrado. Gracias.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAccepting(false); }
  };

  useEffect(() => {
    tick.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick.current);
  }, []);

  const getLocation = (mode) => new Promise((resolve, reject) => {
    if (!navigator.geolocation || mode === "off") return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }),
      () => (mode === "required" ? reject(new Error("denied")) : resolve(null)),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });

  const action = async (path, label) => {
    setBusy(true);
    try {
      let loc = null;
      const geoMode = status?.geo_mode || "optional";
      if ((path === "in" || path === "out") && geoMode !== "off") {
        try {
          loc = await getLocation(geoMode);
        } catch {
          toast.error("Tu empresa requiere compartir la ubicación para fichar. Activa el permiso de ubicación e inténtalo de nuevo.");
          return;
        }
      }
      const body = path === "in" ? { work_mode: workMode, ...(loc || {}) } : (loc || {});
      await api.post(`/clock/${path}`, body);
      toast.success(label);
      await loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitReq = async () => {
    if (!req.start_date || !req.end_date) return toast.error("Indica las fechas");
    try {
      await api.post("/absences", req);
      toast.success("Solicitud enviada");
      setReqOpen(false); setReq({ type: "vacation", start_date: "", end_date: "", reason: "" });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // live elapsed
  let elapsed = status?.worked_minutes || 0;
  if (status?.clocked_in && !status?.on_break && status?.clock_in) {
    const base = status.worked_minutes || 0;
    elapsed = base; // already computed server-side at fetch; add live seconds
  }
  const liveExtra = status?.clocked_in && !status?.on_break ? (now % 1000) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Hola, {user.name.split(" ")[0]} 👋</h1>
        <p className="text-slate-500 text-sm mt-1">Tu portal personal de fichaje y solicitudes.</p>
      </div>

      <Tabs defaultValue="clock">
        <TabsList>
          <TabsTrigger value="clock" data-testid="tab-clock">Fichaje</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours">Mis Horas</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Mi Horario</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Solicitudes</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Gastos</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documentos</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">Salud</TabsTrigger>
          <TabsTrigger value="badge" data-testid="tab-badge">Mi Carnet</TabsTrigger>
          <TabsTrigger value="lopd" data-testid="tab-lopd">Protección de datos</TabsTrigger>
        </TabsList>

        {/* FICHAJE */}
        <TabsContent value="clock" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1 p-8 border-slate-200 shadow-none text-center">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">Estado actual</div>
              <div className="font-heading text-5xl font-bold tabular-nums mb-1" style={{ color: status?.clocked_in ? "var(--brand)" : "#94A3B8" }}>
                {new Date(now).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </div>
              {status?.clocked_in ? (
                <Badge className={status.on_break ? "bg-amber-100 text-amber-700 border-0" : "bg-emerald-100 text-emerald-700 border-0"}>
                  {status.on_break ? "En pausa" : "Trabajando"}
                </Badge>
              ) : <Badge variant="secondary">Fuera de turno</Badge>}
              {status?.clocked_in && (
                <div className="mt-4 text-sm text-slate-500">Trabajado hoy: <span className="font-semibold text-slate-800">{fmtMin(status.worked_minutes)}</span></div>
              )}
              {status?.clocked_in && status?.work_mode && (
                <div className="mt-2 flex justify-center">
                  <Badge variant="outline" className={status.work_mode === "remote" ? "text-violet-600 border-violet-200 gap-1" : "text-sky-600 border-sky-200 gap-1"}>
                    {status.work_mode === "remote" ? <><Home className="h-3 w-3" /> Teletrabajo</> : <><Building2 className="h-3 w-3" /> Presencial</>}
                  </Badge>
                </div>
              )}
              {!status?.clocked_in && profile?.allow_remote && (
                <div className="mt-5 grid grid-cols-2 gap-2" data-testid="work-mode-selector">
                  <button onClick={() => setWorkMode("office")} data-testid="mode-office"
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${workMode === "office" ? "border-sky-300 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    <Building2 className="h-4 w-4" /> Presencial
                  </button>
                  <button onClick={() => setWorkMode("remote")} data-testid="mode-remote"
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${workMode === "remote" ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    <Home className="h-4 w-4" /> Teletrabajo
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-6">
                {!status?.clocked_in ? (
                  <Button disabled={busy} onClick={() => action("in", "Entrada registrada")} data-testid="clock-in-btn" className="col-span-2 h-14 brand-btn text-base">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5 mr-2" /> Fichar entrada</>}
                  </Button>
                ) : (
                  <>
                    {!status.on_break ? (
                      <Button disabled={busy} onClick={() => action("break-start", "Pausa iniciada")} data-testid="break-start-btn" variant="outline" className="h-14 border-amber-200 text-amber-700 hover:bg-amber-50"><Coffee className="h-5 w-5 mr-2" /> Pausa</Button>
                    ) : (
                      <Button disabled={busy} onClick={() => action("break-end", "Pausa finalizada")} data-testid="break-end-btn" variant="outline" className="h-14 border-emerald-200 text-emerald-700 hover:bg-emerald-50"><Play className="h-5 w-5 mr-2" /> Reanudar</Button>
                    )}
                    <Button disabled={busy} onClick={() => action("out", "Salida registrada")} data-testid="clock-out-btn" className="h-14 bg-slate-900 hover:bg-slate-800 text-white"><LogOut className="h-5 w-5 mr-2" /> Salida</Button>
                  </>
                )}
              </div>
            </Card>

            <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
              <KpiCard testId="me-month" label="Horas este mes" value={summary?.month_hours ?? "—"} suffix="h" icon={Clock} accent />
              <KpiCard testId="me-overtime" label="Horas extra" value={summary?.overtime_hours ?? "—"} suffix="h" icon={Timer} />
              <KpiCard testId="me-days" label="Días trabajados" value={summary?.days_worked ?? "—"} icon={CalendarDays} />
              <KpiCard testId="me-pending" label="Solicitudes pendientes" value={summary?.pending_requests ?? "—"} icon={Hourglass} />
            </div>
          </div>
        </TabsContent>

        {/* MIS HORAS */}
        <TabsContent value="hours" className="mt-6 space-y-6">
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-heading text-lg font-semibold mb-4">Horas diarias (este mes)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary?.series || []}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={30} />
                <Tooltip />
                <Bar dataKey="hours" name="Horas" fill="var(--brand)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-heading text-lg font-semibold mb-4">Últimos fichajes</h3>
            <div className="space-y-2">
              {shifts.slice(0, 12).map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                  <span className="font-medium text-slate-700">{new Date(s.clock_in).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" })}</span>
                  <span className="text-slate-500">{new Date(s.clock_in).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} → {s.clock_out ? new Date(s.clock_out).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                  <span className="font-semibold text-slate-800 tabular-nums">{fmtMin(s.worked_minutes || 0)}</span>
                  {s.overtime_minutes > 0 && <Badge variant="outline" className="text-amber-600 border-amber-200">+{fmtMin(s.overtime_minutes)}</Badge>}
                </div>
              ))}
              {!shifts.length && <div className="text-center text-slate-400 py-6">Aún no tienes fichajes.</div>}
            </div>
          </Card>
        </TabsContent>

        {/* MI HORARIO */}
        <TabsContent value="schedule" className="mt-6">
          <Card className="p-8 border-slate-200 shadow-none max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-11 w-11 rounded-xl bg-sky-50 flex items-center justify-center"><Clock className="h-6 w-6 text-sky-500" /></div>
              <div>
                <h3 className="font-heading text-lg font-semibold">{schedule ? schedule.name : "Horario asignado"}</h3>
                <p className="text-xs text-slate-400">{schedule?.flexible ? "Horario flexible · cumple las horas teóricas" : "Tu jornada laboral semanal"}</p>
              </div>
            </div>
            {schedule ? (
              <div className="space-y-2">
                {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"].map((wd, i) => {
                  const d = (schedule.weekly || []).find((x) => x.weekday === i) || {};
                  return (
                    <div key={i} className={`flex items-center justify-between rounded-lg px-4 py-2.5 ${d.enabled ? "bg-slate-50" : "bg-white border border-dashed border-slate-100"}`} data-testid={`my-sched-day-${i}`}>
                      <span className={`text-sm font-medium ${d.enabled ? "text-slate-700" : "text-slate-300"}`}>{wd}</span>
                      {d.enabled ? (
                        <span className="text-sm text-slate-600 tabular-nums">{d.start} – {d.end} <span className="text-slate-400">· pausa {d.break_minutes}m</span></span>
                      ) : <span className="text-xs text-slate-300">Libre</span>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-slate-400 py-8">Aún no tienes un horario asignado. Consulta con tu responsable.</div>
            )}
          </Card>
        </TabsContent>

        {/* SOLICITUDES */}
        <TabsContent value="requests" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading text-lg font-semibold">Mis solicitudes</h3>
            <Dialog open={reqOpen} onOpenChange={setReqOpen}>
              <DialogTrigger asChild><Button className="brand-btn" data-testid="new-request-btn"><Plus className="h-4 w-4 mr-2" /> Nueva solicitud</Button></DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle className="font-heading">Solicitar ausencia</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={req.type} onValueChange={(v) => setReq({ ...req, type: v })}>
                      <SelectTrigger className="mt-1" data-testid="req-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{ABS_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Desde</Label><Input type="date" value={req.start_date} onChange={(e) => setReq({ ...req, start_date: e.target.value })} className="mt-1" data-testid="req-start" /></div>
                    <div><Label>Hasta</Label><Input type="date" value={req.end_date} onChange={(e) => setReq({ ...req, end_date: e.target.value })} className="mt-1" data-testid="req-end" /></div>
                  </div>
                  <div><Label>Motivo (opcional)</Label><Textarea value={req.reason} onChange={(e) => setReq({ ...req, reason: e.target.value })} className="mt-1" data-testid="req-reason" /></div>
                </div>
                <DialogFooter><Button onClick={submitReq} className="brand-btn" data-testid="submit-request-btn">Enviar solicitud</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid gap-3">
            {absences.map((a) => (
              <Card key={a.id} className="p-4 border-slate-200 shadow-none flex items-center justify-between" data-testid={`my-absence-${a.id}`}>
                <div>
                  <div className="font-semibold text-slate-800">{ABS_LABEL[a.type] || a.type}</div>
                  <div className="text-sm text-slate-500">{new Date(a.start_date).toLocaleDateString("es-ES")} → {new Date(a.end_date).toLocaleDateString("es-ES")}</div>
                </div>
                <Badge className={`${STATUS[a.status]?.c} border-0`}>{STATUS[a.status]?.l}</Badge>
              </Card>
            ))}
            {!absences.length && <Card className="p-10 text-center text-slate-400 border-slate-200 shadow-none">No has realizado solicitudes.</Card>}
          </div>
        </TabsContent>

        {/* GASTOS */}
        <TabsContent value="expenses" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Mis gastos</h3>
              <p className="text-xs text-slate-400 flex items-center gap-1"><Sparkles className="h-3 w-3 text-sky-400" /> Sube la foto del ticket y la IA rellena los datos por ti.</p>
            </div>
            <Dialog open={expOpen} onOpenChange={setExpOpen}>
              <DialogTrigger asChild><Button className="brand-btn" data-testid="new-expense-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo gasto</Button></DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">Registrar gasto</DialogTitle></DialogHeader>
                <ExpenseForm projects={projects} categories={categories} onSaved={() => { setExpOpen(false); loadExpenses(); }} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid gap-3">
            {expenses.map((x) => (
              <Card key={x.id} className="p-4 border-slate-200 shadow-none flex items-center justify-between" data-testid={`my-expense-${x.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 flex items-center gap-1.5">{x.vendor || x.description || "Gasto"} {x.ai_used && <Sparkles className="h-3 w-3 text-sky-400" />}</div>
                  <div className="text-sm text-slate-500">{new Date(x.date + "T00:00:00").toLocaleDateString("es-ES")} · {x.category_name || "Sin categoría"} · {x.payment_label}</div>
                  {x.project_name && <Badge variant="secondary" className="mt-1 text-xs gap-1"><Briefcase className="h-3 w-3" />{x.project_name}</Badge>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold tabular-nums">{Number(x.amount).toFixed(2)} €</div>
                  <Badge className={`${STATUS[x.status]?.c} border-0 mt-1`}>{STATUS[x.status]?.l}</Badge>
                </div>
              </Card>
            ))}
            {!expenses.length && <Card className="p-10 text-center text-slate-400 border-slate-200 shadow-none">Aún no has registrado gastos.</Card>}
          </div>
        </TabsContent>

        {/* DOCUMENTOS */}
        <TabsContent value="documents" className="mt-6">
          <EmployeeDocuments />
        </TabsContent>

        {/* SALUD */}
        <TabsContent value="health" className="mt-6">
          <EmployeeHealth />
        </TabsContent>

        {/* MI CARNET */}
        <TabsContent value="badge" className="mt-6">
          <Card className="p-8 border-slate-200 shadow-none flex flex-col items-center">
            <h3 className="font-heading text-lg font-semibold mb-1">Carnet identificativo</h3>
            <p className="text-sm text-slate-400 mb-6 text-center max-w-sm">Muéstralo a tu responsable o al personal de seguridad cuando te lo requieran. Puedes descargarlo para tenerlo a mano.</p>
            {badge ? <IdBadge data={badge} /> : <Loader2 className="h-6 w-6 animate-spin text-slate-300" />}
          </Card>
        </TabsContent>

        {/* PROTECCIÓN DE DATOS (LOPD) */}
        <TabsContent value="lopd" className="mt-6">
          <Card className="p-8 border-slate-200 shadow-none max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-xl bg-sky-50 flex items-center justify-center"><ShieldCheck className="h-6 w-6 text-sky-500" /></div>
              <div>
                <h3 className="font-heading text-lg font-semibold">{lopd?.title || "Protección de datos"}</h3>
                <p className="text-xs text-slate-400">Información sobre el tratamiento de tus datos personales</p>
              </div>
            </div>
            {lopd ? (
              <>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-5 max-h-72 overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-600 leading-relaxed">{lopd.content}</pre>
                </div>
                {lopd.consent ? (
                  <div className="mt-5 flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="lopd-accepted">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                    <div className="text-sm text-emerald-800">
                      <span className="font-semibold">Has aceptado la cláusula informativa.</span>
                      {lopd.consent_at && <span className="block text-emerald-600 text-xs mt-0.5">El {new Date(lopd.consent_at).toLocaleString("es-ES")}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-amber-50 border border-amber-100">
                    <div className="text-sm text-amber-800">Por favor, lee y acepta la cláusula informativa de protección de datos.</div>
                    <Button onClick={acceptLopd} disabled={accepting} className="brand-btn shrink-0" data-testid="accept-lopd-btn">
                      {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : "He leído y acepto"}
                    </Button>
                  </div>
                )}
              </>
            ) : <Loader2 className="h-6 w-6 animate-spin text-slate-300" />}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
